import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { enUS, hy, ru } from 'date-fns/locale';
import {
	CalendarDays,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Plus,
	Trash2,
	X,
} from 'lucide-react';

import Button from '../../shared/ui/button/button';
import { Spinner } from '../../shared/ui/spinner';
import AddRecordModal from '../../components/AddRecordModal/AddRecordModal';
import { useOverviewRefresh } from '../../context/OverviewRefreshContext';
import { getApiErrorMessage } from '../../shared/api/apiError';
import {
	expensesGerCategoriesRequest,
	getRecurringExpensesRequest,
	recurringExpensesDeleteRequest,
} from '../Expenses/api';
import {
	getRecurringIncomesRequest,
	incomesGerCategoriesRequest,
	recurringIncomesDeleteRequest,
} from '../Income/api';
import {
	addDays,
	buildMonthGrid,
	groupOccurrencesByDate,
	parseDateKey,
	toDateKey,
} from './recurrence';
import styles from './Calendar.module.css';

/**
 * The calendar is a second view onto the recurring rules that already drive
 * Income, Expenses, Transactions and Overview - it holds no transaction data
 * of its own. Editing a chip opens the same <AddRecordModal /> those screens
 * use, which writes back to /recurring-incomes and /recurring-expenses, so a
 * change here is the same change everywhere else.
 *
 * The dates drawn for the future are projections (see ./recurrence.js): a
 * recurring rule only becomes an actual transaction when its notification is
 * accepted, which is what keeps next month's salary out of today's balance.
 *
 * Only recurring records are drawn, so adding from this screen opens the form
 * with the recurring switch already on - a one-off record saved from here
 * would land in Income/Expenses and then be missing from the month you added
 * it to.
 */

/** Chips past this many stack behind a "+N more" that opens the day. */
const MAX_CHIPS_PER_DAY = 3;

/** How far the year picker reaches on either side of this year. */
const YEAR_SPAN = 5;

const DATE_FNS_LOCALES = { en: enUS, hy, ru };
const NUMBER_LOCALES = { en: 'en-US', hy: 'hy-AM', ru: 'ru-RU' };

/** The list endpoints answer as a bare array or wrapped; both shapes happen. */
function unwrap(response, key) {
	return response?.[key] || response?.data || response || [];
}

function startOfMonth(date) {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

export const CalendarView = () => {
	const intl = useIntl();
	const { setRefreshCallback } = useOverviewRefresh();

	const dateLocale = DATE_FNS_LOCALES[intl.locale] || enUS;
	const numberLocale = NUMBER_LOCALES[intl.locale] || 'en-US';

	const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
	const [incomeRules, setIncomeRules] = useState([]);
	const [expenseRules, setExpenseRules] = useState([]);
	const [incomeCategories, setIncomeCategories] = useState([]);
	const [expenseCategories, setExpenseCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);

	const [editing, setEditing] = useState(null); // { type, rule }
	const [creatingOn, setCreatingOn] = useState(null); // "" (no date) | dateKey
	const [openDayKey, setOpenDayKey] = useState('');
	const [pendingDelete, setPendingDelete] = useState(null); // { type, rule }
	const [deleting, setDeleting] = useState(false);

	// Deliberately dependency-free: it is what the refresh effect watches, so a
	// new identity on every render would refetch on every render.
	const loadData = useCallback(async () => {
		setLoadFailed(false);
		try {
			const [incomes, expenses, incomeCats, expenseCats] = await Promise.all([
				getRecurringIncomesRequest(),
				getRecurringExpensesRequest(),
				incomesGerCategoriesRequest(),
				expensesGerCategoriesRequest(),
			]);
			setIncomeRules(unwrap(incomes, 'recurring_incomes'));
			setExpenseRules(unwrap(expenses, 'recurring_expenses'));
			setIncomeCategories(unwrap(incomeCats, 'categories'));
			setExpenseCategories(unwrap(expenseCats, 'categories'));
		} catch (error) {
			console.error('Failed to load the recurring calendar:', error);
			setIncomeRules([]);
			setExpenseRules([]);
			setLoadFailed(true);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	// The sidebar-header's refresh button, and its Add form, drive whichever page is open.
	useEffect(() => {
		setRefreshCallback(loadData);
		return () => setRefreshCallback(null);
	}, [setRefreshCallback, loadData]);

	const todayKey = toDateKey(new Date());

	const grid = useMemo(
		() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth(), 1),
		[cursor],
	);

	const occurrencesByDate = useMemo(() => {
		if (!grid.length) return new Map();
		// Income first so a day reads "+ salary" before its expenses, as in the
		// designs; the grid runs over whole weeks, so the edges of the
		// neighbouring months are filled too.
		const entries = [
			...incomeRules.map(rule => ({ type: 'income', rule })),
			...expenseRules.map(rule => ({ type: 'expense', rule })),
		];
		return groupOccurrencesByDate(
			entries,
			grid[0].dateKey,
			grid[grid.length - 1].dateKey,
		);
	}, [grid, incomeRules, expenseRules]);

	const categoryName = useCallback(
		(type, categoryId) => {
			const list = type === 'income' ? incomeCategories : expenseCategories;
			const found = list.find(
				category => String(category.id) === String(categoryId),
			);
			return found?.name || '';
		},
		[incomeCategories, expenseCategories],
	);

	/** What the chip says: the user's own wording first, the category second. */
	const occurrenceLabel = useCallback(
		occurrence => {
			const { rule, type } = occurrence;
			const written = (rule.description || rule.note || '').trim();
			return (
				written ||
				categoryName(type, rule.category_id) ||
				intl.formatMessage({
					id: type === 'income' ? 'CalendarIDIncome' : 'CalendarIDExpense',
				})
			);
		},
		[categoryName, intl],
	);

	const formatAmount = useCallback(
		(amount, currency, type) => {
			const value = Number(amount) || 0;
			const sign = type === 'income' ? '+' : '-';
			const formatted = new Intl.NumberFormat(numberLocale, {
				maximumFractionDigits: 2,
			}).format(value);
			return `${sign}${formatted}${currency ? ` ${currency}` : ''}`;
		},
		[numberLocale],
	);

	/** Per-currency totals for the visible month; the app is multi-currency, so
      the amounts are never summed across currencies. */
	const monthTotals = useMemo(() => {
		const totals = new Map();
		const month = cursor.getMonth();
		const year = cursor.getFullYear();

		occurrencesByDate.forEach((occurrences, dateKey) => {
			const date = parseDateKey(dateKey);
			if (!date || date.getMonth() !== month || date.getFullYear() !== year) {
				return;
			}
			occurrences.forEach(({ rule, type }) => {
				const currency = rule.currency || '';
				const bucket = totals.get(currency) || {
					currency,
					income: 0,
					expense: 0,
				};
				bucket[type] += Number(rule.amount) || 0;
				totals.set(currency, bucket);
			});
		});

		return [...totals.values()].sort((a, b) =>
			a.currency.localeCompare(b.currency),
		);
	}, [occurrencesByDate, cursor]);

	const weekdayLabels = useMemo(() => {
		const firstMonday = new Date(2024, 0, 1);
		return Array.from({ length: 7 }, (_, index) =>
			format(addDays(firstMonday, index), 'EEE', { locale: dateLocale }),
		);
	}, [dateLocale]);

	const monthOptions = useMemo(
		() =>
			Array.from({ length: 12 }, (_, index) => ({
				value: index,
				label: format(new Date(2024, index, 1), 'LLLL', { locale: dateLocale }),
			})),
		[dateLocale],
	);

	const yearOptions = useMemo(() => {
		const thisYear = new Date().getFullYear();
		const years = new Set();
		for (
			let year = thisYear - YEAR_SPAN;
			year <= thisYear + YEAR_SPAN;
			year++
		) {
			years.add(year);
		}
		// A rule may sit outside the window - keep the shown year selectable.
		years.add(cursor.getFullYear());
		return [...years].sort((a, b) => a - b);
	}, [cursor]);

	const daysWithOccurrences = useMemo(
		() =>
			grid
				.filter(cell => cell.inMonth && occurrencesByDate.has(cell.dateKey))
				.map(cell => ({
					...cell,
					occurrences: occurrencesByDate.get(cell.dateKey),
				})),
		[grid, occurrencesByDate],
	);

	/* Phones get a tapped day rather than the whole month at once. "" means
     "follow the month": today when it is in view, else its first busy day, so
     changing month never leaves a day from the old one selected. */
	const [pickedKey, setPickedKey] = useState('');

	const fallbackKey = useMemo(() => {
		const today = parseDateKey(todayKey);
		if (
			today &&
			today.getMonth() === cursor.getMonth() &&
			today.getFullYear() === cursor.getFullYear()
		) {
			return todayKey;
		}
		return daysWithOccurrences[0]?.dateKey || toDateKey(cursor);
	}, [todayKey, cursor, daysWithOccurrences]);

	const selectedKey = useMemo(() => {
		const picked = parseDateKey(pickedKey);
		const sameMonth =
			picked &&
			picked.getMonth() === cursor.getMonth() &&
			picked.getFullYear() === cursor.getFullYear();
		return sameMonth ? pickedKey : fallbackKey;
	}, [pickedKey, cursor, fallbackKey]);

	const selectedDate = parseDateKey(selectedKey) || cursor;
	const selectedOccurrences = occurrencesByDate.get(selectedKey) || [];

	/* A day from the edge of a neighbouring month moves the month with it. */
	const pickDay = cell => {
		if (!cell.inMonth) setCursor(startOfMonth(cell.date));
		setPickedKey(cell.dateKey);
	};

	const hasAnyRule = incomeRules.length > 0 || expenseRules.length > 0;
	const isCreating = creatingOn !== null;

	const goToMonth = delta =>
		setCursor(current =>
			startOfMonth(
				new Date(current.getFullYear(), current.getMonth() + delta, 1),
			),
		);

	const handleSaved = async () => {
		setEditing(null);
		setCreatingOn(null);
		setOpenDayKey('');
		toast.success(intl.formatMessage({ id: 'CalendarIDSaved' }));
		// Same rules, re-read: a changed frequency or start date lands on new
		// dates as soon as the grid recomputes.
		await loadData();
	};

	const handleConfirmDelete = async () => {
		if (!pendingDelete) return;
		setDeleting(true);
		try {
			if (pendingDelete.type === 'income') {
				await recurringIncomesDeleteRequest(pendingDelete.rule.id);
			} else {
				await recurringExpensesDeleteRequest(pendingDelete.rule.id);
			}
			setPendingDelete(null);
			setOpenDayKey('');
			toast.success(intl.formatMessage({ id: 'CalendarIDDeleted' }));
			await loadData();
		} catch (error) {
			toast.error(
				getApiErrorMessage(
					error,
					intl.formatMessage({ id: 'CalendarIDDeleteError' }),
				),
			);
		} finally {
			setDeleting(false);
		}
	};

	/* `compact` is the month grid: one line, amount only, no delete - the cell is
     too narrow to hold one, and the day's own list is a click away. */
	const renderChip = (
		occurrence,
		{ showDelete = false, compact = false } = {},
	) => {
		const { rule, type, key } = occurrence;
		const label = occurrenceLabel(occurrence);
		const amount = formatAmount(rule.amount, rule.currency, type);

		return (
			<div
				key={key}
				className={`${styles.chip} ${
					type === 'income' ? styles.chipIncome : styles.chipExpense
				}`}
			>
				<button
					type='button'
					className={styles.chipMain}
					onClick={() => setEditing({ type, rule })}
					title={`${label} ${amount}`}
				>
					<span className={styles.chipLabel}>{label}</span>
					<span className={styles.chipAmount}>{amount}</span>
				</button>
				{!compact && (
					<button
						type='button'
						className={`${styles.chipDelete} ${
							showDelete ? styles.chipDeleteVisible : ''
						}`}
						onClick={() => setPendingDelete({ type, rule })}
						aria-label={intl.formatMessage(
							{ id: 'CalendarIDDeleteAria' },
							{ name: label },
						)}
					>
						<Trash2 size={13} strokeWidth={1.8} aria-hidden />
					</button>
				)}
			</div>
		);
	};

	const openDayOccurrences = openDayKey
		? occurrencesByDate.get(openDayKey) || []
		: [];

	if (loading) return <Spinner />;

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.toolbar}>
					<h1 className={styles.title}>
						<CalendarDays size={22} strokeWidth={1.8} aria-hidden />
						{intl.formatMessage({ id: 'CalendarIDTitle' })}
					</h1>

					<div className={styles.pickers}>
						<div className={`${styles.pill} ${styles.monthPill}`}>
							<select
								className={styles.pillSelect}
								aria-label={intl.formatMessage({ id: 'CalendarIDMonth' })}
								value={cursor.getMonth()}
								onChange={event =>
									setCursor(
										new Date(
											cursor.getFullYear(),
											Number(event.target.value),
											1,
										),
									)
								}
							>
								{monthOptions.map(month => (
									<option key={month.value} value={month.value}>
										{month.label}
									</option>
								))}
							</select>
							<ChevronDown size={16} strokeWidth={2} aria-hidden />
						</div>

						<div className={styles.pill}>
							<select
								className={styles.pillSelect}
								aria-label={intl.formatMessage({ id: 'CalendarIDYear' })}
								value={cursor.getFullYear()}
								onChange={event =>
									setCursor(
										new Date(Number(event.target.value), cursor.getMonth(), 1),
									)
								}
							>
								{yearOptions.map(year => (
									<option key={year} value={year}>
										{year}
									</option>
								))}
							</select>
							<ChevronDown size={16} strokeWidth={2} aria-hidden />
						</div>

						<Button
							variant='ghost'
							size='sm'
							onClick={() => setCursor(startOfMonth(new Date()))}
						>
							{intl.formatMessage({ id: 'CalendarIDToday' })}
						</Button>
					</div>

					<Button size='sm' onClick={() => setCreatingOn('')}>
						<Plus size={16} strokeWidth={2} aria-hidden />
						{intl.formatMessage({ id: 'CalendarIDAdd' })}
					</Button>
				</div>

				{monthTotals.length > 0 && (
					<p className={styles.summary}>
						{monthTotals.map(total => (
							<span key={total.currency} className={styles.summaryItem}>
								<span className={styles.summaryCurrency}>
									{total.currency || '—'}
								</span>
								<span className={styles.summaryIncome}>
									{formatAmount(total.income, '', 'income')}
								</span>
								<span className={styles.summaryExpense}>
									{formatAmount(total.expense, '', 'expense')}
								</span>
							</span>
						))}
					</p>
				)}

				{loadFailed && (
					<p className={styles.errorBanner}>
						{intl.formatMessage({ id: 'CalendarIDLoadError' })}
					</p>
				)}
				<>
					<div className={styles.weekRow}>
						<button
							type='button'
							className={styles.navCircle}
							onClick={() => goToMonth(-1)}
							aria-label={intl.formatMessage({ id: 'CalendarIDPrevMonth' })}
						>
							<ChevronLeft size={18} strokeWidth={2} aria-hidden />
						</button>
						<div className={styles.weekdays}>
							{weekdayLabels.map(label => (
								<span key={label} className={styles.weekday}>
									{label}
								</span>
							))}
						</div>
						<span className={styles.monthLabel}>
							{format(cursor, 'LLLL', { locale: dateLocale })}
						</span>
						<button
							type='button'
							className={styles.navCircle}
							onClick={() => goToMonth(1)}
							aria-label={intl.formatMessage({ id: 'CalendarIDNextMonth' })}
						>
							<ChevronRight size={18} strokeWidth={2} aria-hidden />
						</button>
					</div>

					<div className={styles.days}>
						{grid.map(cell => {
							const occurrences = occurrencesByDate.get(cell.dateKey) || [];
							const visible = occurrences.slice(0, MAX_CHIPS_PER_DAY);
							const hidden = occurrences.length - visible.length;

							return (
								<div
									key={cell.dateKey}
									className={`${styles.day} ${
										cell.inMonth ? '' : styles.dayOutside
									} ${cell.dateKey === todayKey ? styles.dayToday : ''}`}
								>
									<span className={styles.dayNumber}>
										{cell.date.getDate()}
									</span>
									<div className={styles.dayChips}>
										{visible.map(occurrence =>
											renderChip(occurrence, { compact: true }),
										)}
										{hidden > 0 && (
											<button
												type='button'
												className={styles.moreButton}
												onClick={() => setOpenDayKey(cell.dateKey)}
											>
												{intl.formatMessage(
													{ id: 'CalendarIDMore' },
													{ count: hidden },
												)}
											</button>
										)}
									</div>
									{cell.inMonth && (
										<button
											type='button'
											className={styles.dayAdd}
											onClick={() => setCreatingOn(cell.dateKey)}
											aria-label={intl.formatMessage(
												{ id: 'CalendarIDAddOn' },
												{
													date: format(cell.date, 'd MMMM', {
														locale: dateLocale,
													}),
												},
											)}
										>
											<Plus size={14} strokeWidth={2} aria-hidden />
										</button>
									)}
								</div>
							);
						})}
					</div>

					{/* Phones: the month stays a month - a compact grid of dots - and
                the day you tap is listed underneath it at a readable size. A
                seven-column grid cannot hold a chip you could read. */}
					<div className={styles.mini}>
						<div className={styles.miniWeekdays}>
							{weekdayLabels.map(label => (
								<span key={label}>{label}</span>
							))}
						</div>

						<div className={styles.miniGrid}>
							{grid.map(cell => {
								const occurrences = occurrencesByDate.get(cell.dateKey) || [];
								const hasIncome = occurrences.some(o => o.type === 'income');
								const hasExpense = occurrences.some(o => o.type === 'expense');

								return (
									<button
										key={cell.dateKey}
										type='button'
										className={`${styles.miniDay} ${
											cell.inMonth ? '' : styles.miniOutside
										} ${cell.dateKey === todayKey ? styles.miniToday : ''} ${
											cell.dateKey === selectedKey ? styles.miniSelected : ''
										}`}
										onClick={() => pickDay(cell)}
										aria-pressed={cell.dateKey === selectedKey}
										aria-label={format(cell.date, 'd MMMM', {
											locale: dateLocale,
										})}
									>
										<span className={styles.miniNumber}>
											{cell.date.getDate()}
										</span>
										<span className={styles.miniDots} aria-hidden>
											{hasIncome && <i className={styles.dotIncome} />}
											{hasExpense && <i className={styles.dotExpense} />}
										</span>
									</button>
								);
							})}
						</div>

						<section className={styles.dayPanel}>
							<h3 className={styles.dayPanelDate}>
								{format(selectedDate, 'd MMMM, EEEE', { locale: dateLocale })}
							</h3>

							{selectedOccurrences.length > 0 ? (
								<div className={styles.agendaChips}>
									{selectedOccurrences.map(occurrence =>
										renderChip(occurrence, { showDelete: true }),
									)}
								</div>
							) : (
								<p className={styles.dayPanelEmpty}>
									{intl.formatMessage({ id: 'CalendarIDEmptyDay' })}
								</p>
							)}

							<Button
								variant='secondary'
								size='sm'
								fullWidth
								onClick={() => setCreatingOn(selectedKey)}
							>
								<Plus size={16} strokeWidth={2} aria-hidden />
								{intl.formatMessage({ id: 'CalendarIDAdd' })}
							</Button>
						</section>
					</div>

					{!hasAnyRule && !loadFailed ? (
						<div className={styles.emptyState}>
							<h3 className={styles.emptyTitle}>
								{intl.formatMessage({ id: 'CalendarIDEmptyTitle' })}
							</h3>
							<p className={styles.emptyText}>
								{intl.formatMessage({ id: 'CalendarIDEmptyText' })}
							</p>
							<Button size='sm' onClick={() => setCreatingOn('')}>
								<Plus size={16} strokeWidth={2} aria-hidden />
								{intl.formatMessage({ id: 'CalendarIDAdd' })}
							</Button>
						</div>
					) : (
						daysWithOccurrences.length === 0 && (
							<p className={styles.emptyMonth}>
								{intl.formatMessage({ id: 'CalendarIDEmptyMonth' })}
							</p>
						)
					)}
				</>
			</div>

			{openDayKey && (
				<div
					className={styles.modalOverlay}
					onClick={() => setOpenDayKey('')}
					role='presentation'
				>
					<div
						className={styles.dayModal}
						onClick={event => event.stopPropagation()}
					>
						<div className={styles.dayModalHeader}>
							<h3>
								{format(parseDateKey(openDayKey) || new Date(), 'd MMMM yyyy', {
									locale: dateLocale,
								})}
							</h3>
							<button
								type='button'
								className={styles.iconButton}
								onClick={() => setOpenDayKey('')}
								aria-label={intl.formatMessage({ id: 'CalendarIDCancel' })}
							>
								<X size={18} strokeWidth={1.8} aria-hidden />
							</button>
						</div>
						<div className={styles.dayModalChips}>
							{openDayOccurrences.map(occurrence =>
								renderChip(occurrence, { showDelete: true }),
							)}
						</div>
					</div>
				</div>
			)}

			{pendingDelete && (
				<div className={styles.modalOverlay} role='presentation'>
					<div className={styles.confirmModal}>
						<h3 className={styles.confirmTitle}>
							{intl.formatMessage({ id: 'CalendarIDDeleteTitle' })}
						</h3>
						<p className={styles.confirmText}>
							{intl.formatMessage({ id: 'CalendarIDDeleteText' })}
						</p>
						<div className={styles.confirmActions}>
							<Button
								variant='danger'
								loading={deleting}
								onClick={handleConfirmDelete}
							>
								{intl.formatMessage({
									id: deleting ? 'CalendarIDDeleting' : 'CalendarIDDelete',
								})}
							</Button>
							<Button
								variant='secondary'
								disabled={deleting}
								onClick={() => setPendingDelete(null)}
							>
								{intl.formatMessage({ id: 'CalendarIDCancel' })}
							</Button>
						</div>
					</div>
				</div>
			)}

			<AddRecordModal
				open={!!editing || isCreating}
				onClose={() => {
					setEditing(null);
					setCreatingOn(null);
				}}
				onSuccess={handleSaved}
				defaultTab={editing?.type === 'expense' ? 'expense' : 'income'}
				editingIncome={editing?.type === 'income' ? editing.rule : null}
				editingExpense={editing?.type === 'expense' ? editing.rule : null}
				// A record added from here has to be recurring, or it would be saved
				// and then not appear on the calendar at all.
				defaultRecurring={isCreating}
				defaultDate={creatingOn || null}
			/>
		</div>
	);
};

export default CalendarView;
